import type {
	AIUnifiedCommonAttributes,
	AIUnifiedEventPayload,
} from '@atlaskit/editor-common/analytics';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { fg } from '@atlaskit/platform-feature-flags';

function createAIUnifiedCommonAttributes(
	interactionID: string | undefined,
	experienceName?: string,
	isProactive?: boolean,
	traceIds?: string[],
	invokedFrom?: string,
	nudgeMetrics?: Record<string, number | undefined>,
	has3pSources?: boolean,
	promptVersion?: string,
	sourceProduct?: string | null,
): AIUnifiedCommonAttributes {
	return {
		singleInstrumentationID: interactionID ?? '',
		aiInteractionID: interactionID ?? '',
		aiFeatureName: 'Editor AI',
		aiExperienceName: experienceName ?? 'unknown',
		has3pSources,
		isAIFeature: 1,
		proactiveAIGenerated: !!isProactive ? 1 : 0,
		userGeneratedAI: !isProactive ? 1 : 0,
		sourceProduct,
		traceIds,
		invokedFrom,
		nudgeMetrics,
		promptVersion,
	};
}

/**
 * Utility types to extract only the non-common attributes
 * * OmitCommonAttributes: Removes AIUnifiedCommonAttributes from the unified attributes
 *   * We also distribute the Omit across the union
 * * ExcludeEmpty: Editor Common AEP Utilities also extends attributes with & {}
 *   * {} allows any properties to be added
 *   * We are also sometimes left with {} after using Omit
 *   * This util drops any {} types from the union
 */

type ExcludeEmpty<T> = T extends unknown ? { [K in keyof T]: Pick<T, K> }[keyof T] : never;
type OmitCommonAttributes<T> = T extends AIUnifiedCommonAttributes
	? ExcludeEmpty<Omit<T, keyof AIUnifiedCommonAttributes>>
	: never;

export function createUnifiedAnalyticsPayload({
	action,
	interactionID,
	attributes,
	experienceName,
	has3pSources,
	isProactive,
	invokedFrom,
	traceIds,
	nudgeMetrics,
	// Remove below flag when AIFC is fully rolled out in Jira, only required for old experience in Jira.
	isStreamingEnded,
	aiGroupId,
	isPayloadCreate,
	promptVersion,
	sourceProduct,
}: {
	action: AIUnifiedEventPayload['action'];
	aiGroupId?: string;
	attributes?: OmitCommonAttributes<AIUnifiedEventPayload['attributes']>;
	experienceName?: string;
	has3pSources?: boolean;
	interactionID: string | undefined;
	invokedFrom?: string;
	isPayloadCreate?: boolean;
	isProactive?: boolean;
	// Remove below flag when AIFC is fully rolled out in Jira, only required for old experience in Jira.
	isStreamingEnded?: boolean;
	nudgeMetrics?: Record<string, number | undefined>;
	promptVersion?: string;
	sourceProduct?: string | null;
	traceIds?: string[];
}): AIUnifiedEventPayload {
	let unifiedAnalyticsAttributes = createAIUnifiedCommonAttributes(
		interactionID,
		experienceName,
		isProactive,
		traceIds,
		invokedFrom,
		nudgeMetrics,
		has3pSources,
		promptVersion,
		sourceProduct,
	);
	// Remove below flag when AIFC is fully rolled out in Jira, only required for old experience in Jira.
	if (fg('platform_editor_ai_streaming_ended_analytics_attr')) {
		unifiedAnalyticsAttributes = {
			...unifiedAnalyticsAttributes,
			isStreamingEnded,
		};
	}

	switch (action) {
		case ACTION.ACTIONED: {
			return {
				action,
				actionSubject: ACTION_SUBJECT.AI_RESULT,
				actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					aiResultAction: 'unknown',
					aiGroupId,
					...(isPayloadCreate !== undefined && { isPayloadCreate }),
					...unifiedAnalyticsAttributes,
					...attributes,
				},
			};
		}
		case ACTION.VIEWED:
		case ACTION.ERROR: {
			return {
				action,
				actionSubject: ACTION_SUBJECT.AI_RESULT,
				actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					...unifiedAnalyticsAttributes,
					...attributes,
				},
			};
		}
		case ACTION.INITIATED:
		case ACTION.DISMISSED: {
			return {
				action,
				actionSubject: ACTION_SUBJECT.AI_INTERACTION,
				actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					...unifiedAnalyticsAttributes,
					...attributes,
				},
			};
		}
		case ACTION.SUBMITTED: {
			return {
				action,
				actionSubject: ACTION_SUBJECT.AI_FEEDBACK,
				actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					// If feedback result is not explicitly set then we should assume negative, since people are more likely to
					// report negative experiences than they are positive ones.
					aiFeedbackResult: 'down',
					...unifiedAnalyticsAttributes,
					...attributes,
				},
			};
		}
		case ACTION.DISCARDED: {
			return {
				action,
				actionSubject: ACTION_SUBJECT.AI_RESULT,
				actionSubjectId: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI,
				eventType: EVENT_TYPE.TRACK,
				attributes: {
					...unifiedAnalyticsAttributes,
					...attributes,
				},
			};
		}
	}
}
